import { useState } from "react";
import { toast } from "sonner";
import { Scale, Calculator, Utensils, ClipboardList, GraduationCap } from "lucide-react";
import { gradeNumeric, optPts, weightLossPlan } from "@/data/nutritionCases";
import {
  BeatShell,
  NumberField,
  OptionList,
  ChipRow,
  fmt0,
  fmt1,
} from "@/components/nutrition/NutritionParts";
import { BodyConditionModel } from "@/components/nutrition/BodyConditionModel";
import { PortionVisualiser, WeightTrendChart } from "@/components/nutrition/NutritionVisuals";

const Given = ({ label, value }) => (
  <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-mono-clinical text-sm text-slate-900 font-semibold leading-tight mt-0.5">{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
    {children}
  </div>
);

/* ── Beat 1 · Assess ──────────────────────────────────────────────────────── */
export const AssessBeat = ({ c, onDone }) => {
  const [bcs, setBcs] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;

  const toggle = (i) => {
    const n = new Set(sel);
    n.has(i) ? n.delete(i) : n.add(i);
    setSel(n);
  };

  const grade = () => {
    const bcsPts = bcs === c.bcs ? 10 : bcs != null && Math.abs(bcs - c.bcs) === 1 ? 5 : 0;
    const trueIdx = c.concerns.map((x, i) => (x.flag ? i : -1)).filter((i) => i >= 0);
    const caught = trueIdx.filter((i) => sel.has(i)).length;
    const falseTaps = [...sel].filter((i) => !c.concerns[i].flag).length;
    const flagPts = Math.max(0, Math.min(10, Math.round((10 * caught) / trueIdx.length) - 3 * falseTaps));
    return {
      earned: bcsPts + flagPts,
      max: 20,
      detail: `BCS ${bcs ?? "—"}/9 (ideal answer ${c.bcs}); concerns ${caught}/${trueIdx.length}, ${falseTaps} false`,
      key: "assess",
      label: "Assess",
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (bcs == null) return toast.error("Assign a body condition score.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Scale}
      eyebrow="Stage 1 of 5 · Assess"
      title="Nutritional assessment"
      subtitle={`${c.name} — ${c.signalment}. ${c.summary}`}
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="assess-primary-btn"
    >
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="assess-history">
        {c.history}
      </div>
      <Section title={`Body condition score (current weight ${fmt1(c.weightKg)} kg)`}>
        <BodyConditionModel value={bcs} onChange={setBcs} correct={c.bcs} revealed={checked} species={c.species} />
        <div className="text-[11px] text-slate-500 mt-1.5">Read the silhouette from above and the side — set the score where the waist and abdominal tuck match.</div>
      </Section>
      <Section title="Tap the nutritional concerns in the history">
        <ChipRow chips={c.concerns} selected={sel} onToggle={toggle} revealed={checked} testidPrefix="concern-chip" />
        {checked && (
          <div className="mt-3 space-y-1.5" data-testid="assess-coaching">
            {c.concerns.filter((x) => x.flag).map((x, i) => (
              <div key={i} className="text-[11px] text-slate-600 leading-snug">
                <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
              </div>
            ))}
          </div>
        )}
      </Section>
    </BeatShell>
  );
};

/* ── Beat 2 · Calculate RER & MER ─────────────────────────────────────────── */
export const CalcBeat = ({ c, solved, onDone }) => {
  const [rerVal, setRer] = useState("");
  const [factorIdx, setFactorIdx] = useState(null);
  const [merVal, setMer] = useState("");
  const [res, setRes] = useState(null);
  const checked = !!res;

  const factorVal = factorIdx != null ? c.calc.factorOptions[factorIdx].value : null;
  const merAutofill = rerVal !== "" && factorVal != null ? Math.round(parseFloat(rerVal) * factorVal) : null;

  const grade = () => {
    const rerRes = gradeNumeric(rerVal, solved.RER, 15);
    const factorOk = c.calc.factorOptions[factorIdx].correct;
    const merRes = gradeNumeric(merVal, solved.MER, 15);
    return {
      rerRes,
      merRes,
      scoreObj: {
        earned: rerRes.pts + (factorOk ? 10 : 0) + merRes.pts,
        max: 40,
        detail: `RER ${fmt0(solved.RER)} kcal · factor ×${solved.factor} · MER ${fmt0(solved.MER)} kcal`,
        key: "calc",
        label: "Calculate RER & MER",
      },
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (rerVal === "" || merVal === "") return toast.error("Enter both RER and MER.");
      if (factorIdx == null) return toast.error("Choose a maintenance factor.");
      setRes(grade());
    } else onDone(res.scoreObj);
  };

  const correctFactorWhy = c.calc.factorOptions.find((o) => o.correct).why;

  return (
    <BeatShell
      Icon={Calculator}
      eyebrow="Stage 2 of 5 · Calculate"
      title="Energy requirements"
      subtitle={c.energyWeightNote}
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="calc-primary-btn"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Given label="Energy weight" value={`${fmt1(solved.ew)} kg`} />
        <Given label="RER formula" value="70 × kg^0.75" />
        <Given label="MER" value="RER × factor" />
      </div>

      <NumberField
        testid="rer-input"
        label="Resting Energy Requirement (RER)"
        unit="kcal/day"
        hint={`70 × ${fmt1(solved.ew)}^0.75`}
        value={rerVal}
        onChange={setRer}
        disabled={checked}
        result={res?.rerRes}
        correctText={`${fmt0(solved.RER)} kcal`}
      />

      <Section title="Maintenance / life-stage factor">
        <OptionList
          options={c.calc.factorOptions.map((o) => ({ t: o.label, score: o.correct ? 3 : 0, note: o.why }))}
          value={factorIdx}
          onPick={setFactorIdx}
          revealed={checked}
          testidPrefix="factor-option"
        />
      </Section>

      <NumberField
        testid="mer-input"
        label="Maintenance Energy Requirement (MER)"
        unit="kcal/day"
        hint="your RER × chosen factor"
        value={merVal}
        onChange={setMer}
        autofillValue={merAutofill}
        onAutofill={(v) => setMer(String(v))}
        disabled={checked}
        result={res?.merRes}
        correctText={`${fmt0(solved.MER)} kcal`}
      />

      {checked && (
        <div className="text-[11px] text-slate-600 leading-snug" data-testid="calc-coaching">
          <span className="text-emerald-600 font-semibold">Correct factor ×{solved.factor}:</span> {correctFactorWhy}
        </div>
      )}
    </BeatShell>
  );
};

/* ── Beat 3 · Select diet ─────────────────────────────────────────────────── */
export const DietBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => {
    const score = c.diet.options[idx].score;
    return {
      earned: optPts(score, 15, 7),
      max: 15,
      detail: `Diet: ${c.diet.options.find((o) => o.score >= 3).t}`,
      key: "diet",
      label: "Select diet",
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose a diet.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Utensils}
      eyebrow="Stage 3 of 5 · Diet"
      title="Diet selection"
      subtitle={c.diet.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="diet-primary-btn"
    >
      <OptionList options={c.diet.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="diet-option" />
    </BeatShell>
  );
};

/* ── Beat 4 · Plan feeding ────────────────────────────────────────────────── */
export const PlanBeat = ({ c, solved, onDone }) => {
  const mode = c.plan.portionMode; // "dry" | "wet" | "liquid"
  const target = mode === "dry" ? solved.grams : mode === "wet" ? solved.cans : solved.ml;
  const ed = c.diet.energyDensity;
  const proj = weightLossPlan(c);

  const [portion, setPortion] = useState("");
  const [mealsIdx, setMealsIdx] = useState(null);
  const [planIdx, setPlanIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => {
    const pRes = gradeNumeric(portion, target, 15);
    const mealsPts = optPts(c.plan.mealsOptions[mealsIdx].score, 5, 2);
    const planPts = optPts(c.plan.planOptions[planIdx].score, 10, 5);
    return {
      pRes,
      scoreObj: {
        earned: pRes.pts + mealsPts + planPts,
        max: 30,
        detail: `${
          mode === "dry" ? `${fmt0(target)} g/day` : mode === "wet" ? `${fmt1(target)} cans/day` : `${fmt0(target)} mL/day`
        } · ${c.plan.mealsOptions.find((o) => o.score >= 3).t}`,
        key: "plan",
        label: "Plan feeding",
      },
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (portion === "") return toast.error("Calculate the portion.");
      if (mealsIdx == null || planIdx == null) return toast.error("Answer every part.");
      setRes(grade());
    } else onDone(res.scoreObj);
  };

  return (
    <BeatShell
      Icon={ClipboardList}
      eyebrow="Stage 4 of 5 · Feeding plan"
      title="Portion & feeding plan"
      subtitle={c.plan.portionNote}
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="plan-primary-btn"
    >
      <div className="grid grid-cols-2 gap-2">
        <Given label="Energy to feed / day" value={`${fmt0(solved.portionKcal)} kcal`} />
        <Given
          label="Diet energy density"
          value={
            mode === "dry"
              ? `${ed.kcalPer100g} kcal / 100 g`
              : mode === "wet"
              ? `${ed.kcalPerCan} kcal / ${ed.canLabel}`
              : `${ed.kcalPerMl.toFixed(1)} kcal / mL`
          }
        />
      </div>

      <NumberField
        testid="portion-input"
        label={mode === "dry" ? "Amount of dry food per day" : mode === "wet" ? "Number of cans per day" : "Volume of liquid diet per day"}
        unit={mode === "dry" ? "g / day" : mode === "wet" ? "cans / day" : "mL / day"}
        hint={
          mode === "dry"
            ? `${fmt0(solved.portionKcal)} kcal ÷ ${ed.kcalPer100g} × 100`
            : mode === "wet"
            ? `${fmt0(solved.portionKcal)} kcal ÷ ${ed.kcalPerCan}`
            : `${fmt0(solved.portionKcal)} kcal ÷ ${ed.kcalPerMl}`
        }
        value={portion}
        onChange={setPortion}
        disabled={checked}
        result={res?.pRes}
        correctText={mode === "dry" ? `${fmt0(target)} g` : mode === "wet" ? `${fmt1(target)} cans` : `${fmt0(target)} mL`}
      />

      <PortionVisualiser mode={mode} target={target} value={portion} revealed={checked} />

      <Section title={c.plan.mealsPrompt}>
        <OptionList options={c.plan.mealsOptions} value={mealsIdx} onPick={setMealsIdx} revealed={checked} testidPrefix="meals-option" />
      </Section>

      <Section title={c.plan.planPrompt}>
        <OptionList options={c.plan.planOptions} value={planIdx} onPick={setPlanIdx} revealed={checked} testidPrefix="plan-option" />
      </Section>

      {checked && proj && (
        <div data-testid="weight-loss-projection" className="rounded-md border-2 border-[#0047AB]/20 bg-[#0047AB]/5 p-3.5">
          <div className="text-[11px] uppercase tracking-widest text-[#0047AB] font-semibold mb-1">
            Projected weight-loss timeline
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">
            {fmt1(proj.current)} kg → <span className="font-semibold">{fmt1(proj.ideal)} kg</span> at ~{proj.ratePct}% / week
            ≈ <span className="font-mono-clinical font-semibold">{proj.weeks} weeks</span> (~{proj.months} months). Recheck weight monthly and re-calculate as he loses.
          </div>
          <WeightTrendChart proj={proj} />
        </div>
      )}
    </BeatShell>
  );
};

/* ── Beat 5 · Educate ─────────────────────────────────────────────────────── */
export const EducateBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.educate.options[idx].score, 10, 5),
    max: 10,
    detail: "Client education message",
    key: "educate",
    label: "Educate",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose a message.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={GraduationCap}
      eyebrow="Stage 5 of 5 · Educate"
      title="Client education"
      subtitle={c.educate.prompt}
      primaryLabel={checked ? "See debrief →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="educate-primary-btn"
    >
      <OptionList options={c.educate.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="educate-option" />
    </BeatShell>
  );
};
